import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { TreeNode } from '../analytics/slice'

/**
 * THE CATALOGUE TREE — how you get to ONE season's or ONE episode's figures.
 *
 * ★★ THIS IS NOT THE SAME CONTROL AS THE DEPTH AXIS, and both are wanted (David, 2026-07-23). The
 * depth axis re-levels the RANKINGS, so every row in the chart is the same kind of thing and you
 * can ask "which of my episodes did best". The tree navigates to a single node, so you can ask
 * "how did Season 1 do". Different questions; v1 shipped both, in a select and in its menu.
 *
 * ★★ EVERY LEVEL HAS ITS OWN DESTINATION. v1's menu gives each title an `Overview` leaf beside its
 * `Breakdown` branch — the node's own roll-up as a place you can stand, distinct from the list of
 * its children. Here the row IS that destination and the twisty is the branch, which collapses
 * v1's two entries into one without losing either behaviour.
 *
 * ⚠ THE TREE IS NEVER FILTERED BY THE TYPE AXIS. A tree you cannot navigate to the bottom of is a
 * broken tree, not a filtered one — the filter belongs to the rankings.
 *
 * ⚠ SYNTHETIC NODES ARE MARKED AND CARRY NO RANK. "Supporting material" is a grouping this client
 * invents; it has real figures (the sum of its items) but no position, because rank is computed
 * server-side per level and an invented level has no level to be ranked within.
 */

const twistyChar = (open: boolean) => (open ? '▾' : '▸')

const meta: CSSProperties = {
  flex: '0 0 auto',
  fontSize: 'var(--fs-xs)',
  color: 'var(--muted-foreground)',
  fontFamily: 'var(--font-number)',
}

function Row({
  node,
  selectedKey,
  onSelect,
  depth,
}: {
  node: TreeNode
  selectedKey: string | null
  onSelect: (node: TreeNode) => void
  depth: number
}) {
  // ★ Open by default down to the first level, so the shape of the catalogue is visible without
  // a single click, but a long series does not dump 40 episodes on you either.
  const [open, setOpen] = useState(depth < 1)
  const hasChildren = node.children.length > 0

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: depth * 14 }}>
        {hasChildren ? (
          <button
            type="button"
            className="dash-tree-twist"
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${node.title}`}
            onClick={() => setOpen((o) => !o)}
          >
            {twistyChar(open)}
          </button>
        ) : (
          <span className="dash-tree-twist" aria-hidden="true" />
        )}
        <button
          type="button"
          className="dash-tree-row"
          data-selected={selectedKey === node.key ? 'true' : 'false'}
          onClick={() => onSelect(node)}
        >
          <span
            className="dash-tree-label"
            style={{ fontStyle: node.synthetic ? 'italic' : undefined }}
            title={node.title}
          >
            {node.title}
          </span>
          {/* ⚠ A node nobody watched says so, rather than showing a 0 that looks like a
              measurement — the same rule the titles grid already holds. */}
          <span style={meta}>{node.watchSeconds > 0 ? `${node.views}` : '—'}</span>
        </button>
      </div>
      {open &&
        node.children.map((child) => (
          <Row key={child.key} node={child} selectedKey={selectedKey} onSelect={onSelect} depth={depth + 1} />
        ))}
    </>
  )
}

export function CatalogueTree({
  roots,
  selectedKey,
  onSelect,
}: {
  roots: TreeNode[]
  selectedKey: string | null
  onSelect: (node: TreeNode) => void
}) {
  if (roots.length === 0) {
    return <p style={{ ...meta, fontFamily: 'var(--font-standard)', padding: 8 }}>Nothing in the catalogue yet.</p>
  }
  return (
    <div className="dash-tree" role="tree" aria-label="Your catalogue">
      {roots.map((node) => (
        <Row key={node.key} node={node} selectedKey={selectedKey} onSelect={onSelect} depth={0} />
      ))}
    </div>
  )
}
